import {
  AgentByIdResponse,
  GetAgentByIdResponse,
  GetDataSkuListItems,
  GetOrderDetailData,
  GetOrderRepeatInput,
  GetProductListData,
  ItemProductByIdResponse,
  OrderProductsTemporaryItem,
  VariantProductByIdResponse,
} from "services/interface";
import styled from "@emotion/styled";
import {
  Button,
  Col,
  Form,
  Input,
  message,
  Modal,
  Row,
  Select,
  Typography,
} from "antd";
import { Dispatch, FC, SetStateAction, useEffect, useMemo, useState } from "react";
import { PlusOutlined } from "@ant-design/icons";
import { useGetProductSkuList } from "hooks/useProduct";
import OrderReserveProductTable from "./OrderReserveProductTable";
import { useAppSelector } from "store";
import { numberFormat } from "utils/nuberFormat";
import { useGetRepeatOrderRetail } from "hooks/useOrders";

/** จำนวนแพ็กที่สั่งได้สูงสุด (item_qty) จาก stock_balance ÷ product_quantity */
function getMaxOrderablePackQty(sku: GetDataSkuListItems | undefined): number | null {
  if (!sku?.is_stock || !sku.stock) return null;
  const balance = Number(sku.stock.stock_balance) || 0;
  const perPack = Number(sku.product_quantity) || 1;
  if (perPack <= 0) return balance;
  return Math.floor(balance / perPack);
}

function validateOrderQtyAgainstStock(
  sku: GetDataSkuListItems | undefined,
  orderPackQty: number,
  existingPackQtyInCart: number
): string | null {
  if (!sku?.is_stock || !sku.stock) return null;
  const maxPack = getMaxOrderablePackQty(sku);
  const totalPack = existingPackQtyInCart + orderPackQty;
  const balance = Number(sku.stock.stock_balance) || 0;
  const perPack = Number(sku.product_quantity) || 1;

  if (maxPack !== null && maxPack <= 0) {
    return `สินค้านี้สต็อกไม่พอ (คงเหลือ ${numberFormat(balance)} ชิ้น)`;
  }
  if (maxPack !== null && totalPack > maxPack) {
    const remainingPack = Math.max(0, maxPack - existingPackQtyInCart);
    return remainingPack > 0
      ? `สั่งได้สูงสุดอีก ${remainingPack} แพ็ก (คงเหลือ ${numberFormat(balance)} ชิ้น, ${perPack} ชิ้น/แพ็ก)`
      : `สต็อกไม่พอ — มีในตะกร้า ${existingPackQtyInCart} แพ็กแล้ว (คงเหลือ ${numberFormat(balance)} ชิ้น)`;
  }
  return null;
}

interface MainCreateOrderProductProps {
  data?: GetProductListData[];
  userData?: AgentByIdResponse;
  dataTemporary: OrderProductsTemporaryItem[];
  setDataTemporary: React.Dispatch<
    React.SetStateAction<OrderProductsTemporaryItem[]>
  >;
  canCreate: boolean;
  canDelete: boolean;
  canRepeat?: boolean;
  showStatusColumn: boolean;
  setTotalPrice: Dispatch<SetStateAction<number>>;
  editingKey: string;
  setEditingKey: Dispatch<SetStateAction<string>>;
  currentSelected: string;
  setCurrentSelected: Dispatch<SetStateAction<string>>;
  refType: string;
  refId: string;
  // retailData: AgentByIdResponse;
  order_payment_type?: string;
  item?: GetOrderDetailData;
}

const formItemLayout = {
  labelCol: {
    xs: { span: 24 },
    sm: { span: 8 },
  },
  wrapperCol: {
    xs: { span: 24 },
    sm: { span: 16 },
  },
};

const MainCreateOrderProduct: FC<MainCreateOrderProductProps> = ({
  data,
  dataTemporary,
  setDataTemporary,
  canDelete,
  canCreate,
  showStatusColumn,
  setTotalPrice,
  editingKey,
  setEditingKey,
  currentSelected,
  setCurrentSelected,
  refType,
  refId,
  canRepeat,
  userData,
  order_payment_type,
  item
}) => {
  const factoryId = useAppSelector(
    (state) => state.auth.profile?.user?.role?.ref_id
  );

  const { data: dataSku, isLoading, isError } = useGetProductSkuList();

  const [form] = Form.useForm();
  const [open, setOpen] = useState(false);

  const { mutate: RepeatOrder } = useGetRepeatOrderRetail();

  const [selectSkuid, setSelectSkuId] = useState<string | undefined>(undefined);

  const [isKsher, setIsKsher] = useState<boolean>(false);

  const selectedSku = useMemo(
    () => dataSku?.data.find((s) => s.sku_id === selectSkuid),
    [dataSku?.data, selectSkuid]
  );

  const maxOrderablePackQty = useMemo(
    () => getMaxOrderablePackQty(selectedSku),
    [selectedSku]
  );

  const existingCartPackQty = useMemo(() => {
    if (!selectSkuid) return 0;
    return dataTemporary.find((i) => i.sku_id === selectSkuid)?.qty ?? 0;
  }, [dataTemporary, selectSkuid]);

  const onChangeTotalPrice = (total: number) => {
    setTotalPrice(total);

    console.log(total);
  };

  const repeatOrderRetail = () => {
    if (!refId || !refType) {
      message.warning("กรุณาตรวจสอบข้อมูลลูกค้า");
      return;
    }

    const dataRaw: GetOrderRepeatInput = {
      ref_id: refId || "",
      ref_type: refType || "",
      process: "last_order",
    };
    RepeatOrder(dataRaw, {
      onSuccess: (res) => {
        if (res.status === "success") {
          if (res.data) {
            const newOrderItems = res.data.flatMap((order: any) =>
              order.items.map((item: any) => {
                // หา SKU ปัจจุบันจาก dataSku เพื่อใช้ราคาปัจจุบันตาม refType
                // ใช้ logic เดียวกับที่ใช้ใน Option: refType === "retail" ? wholesale_price : factory_price
                const currentSku = dataSku?.data.find(
                  (sku: GetDataSkuListItems) => sku.sku_id === item.sku_id
                );

                return {
                  product_id: item.product_id,
                  factory_id: order.factory_id,
                  product_name: item.product_name,
                  sku_id: item.sku_id,
                  sku_name: item.sku_description,
                  sku_image: item.image_url,
                  price_id: currentSku?.price_id || item.price_id,
                  factory_price: currentSku?.factory_price || item.factory_price,
                  wholesale_price: currentSku?.wholesale_price || item.wholesale_price,
                  retail_price: currentSku?.retail_price || item.retail_price,
                  vip_price: currentSku?.vip_price || item.vip_price,
                  qty: item.sku_cnt,
                  product_quantity: currentSku?.product_quantity || item.sku_cnt,
                };
              })
            );
            const updatedDataTemporary = newOrderItems.reduce(
              (acc: OrderProductsTemporaryItem[], newItem) => {
                const existingItemIndex = acc.findIndex(
                  (item) => item.sku_id === newItem.sku_id
                );
                if (existingItemIndex !== -1) {
                  acc[existingItemIndex].qty += newItem.qty;
                } else {
                  acc.push(newItem);
                }
                return acc;
              },
              [...dataTemporary]
            );
            setDataTemporary(updatedDataTemporary);
          }
          console.log("res.data", res.data);
          if (!res.data || res.data.length === 0 || res.data[0].items.length === 0) {
            message.warning("ไม่พบข้อมูลการสั่งซื้อล่าสุดหรือสินค้าปิดการขาย");
          } else {
            message.success("เรียกคำสั่งซื้อล่าสุดสำเร็จ");
          }

        

         // message.success("เรียกคำสั่งซื้อล่าสุดสำเร็จ");
        } else {
          message.warning(
            `เรียกคำสั่งซื้อล่าสุดไม่สำเร็จ: ${res.error_message}`
          );
        }
      },
      onError: (error) => {
        message.error(`ไม่สำเร็จ: ${error.message}`);
      },
    });
  };

  const showDrawer = () => {
    setEditingKey && setEditingKey("");
    setOpen(true);
  };
  const onClose = () => {
    setEditingKey && setEditingKey("");
    setCurrentSelected && setCurrentSelected("");
    setOpen(false);
    form.resetFields();
    setSelectSkuId(undefined)
  };

  useEffect(() => {
    let total = 0;
    if (userData && userData.is_vip === false) {
      total = dataTemporary.reduce(
        (total, record) =>
          total +
          (refType === "retail"
            ? record.wholesale_price
            : record.factory_price) *
          record.qty,
        0
      );
    } else if (userData && userData.is_vip === true) {
      total = dataTemporary.reduce(
        (total, record) => total + record.vip_price * record.qty,
        0
      );
    }
    onChangeTotalPrice(Number(total));
  }, [dataTemporary, onChangeTotalPrice]);

  const onFinish = (values: any) => {
    const skuItems = dataSku?.data.find(
      (item) => item.sku_id === values.product_sku_id
    );
    const addQty = Number(values.qty || 1);
    const existingItem = dataTemporary.find(
      (item) => item.sku_id === values.product_sku_id
    );
    const existingPackQty = existingItem ? Number(existingItem.qty || 0) : 0;

    const stockError = validateOrderQtyAgainstStock(
      skuItems,
      addQty,
      existingPackQty
    );
    if (stockError) {
      message.error(stockError);
      return;
    }

    if (existingItem) {
      const newDataTemporary = dataTemporary.map((item) =>
        item.sku_id === existingItem.sku_id
          ? { ...item, qty: Number(item.qty || 1) + Number(values.qty || 1) }
          : item
      );
      setDataTemporary(newDataTemporary);
      form.resetFields();
    } else {
      console.log(skuItems);
      if (skuItems) {
        const newData: OrderProductsTemporaryItem = {
          product_id: skuItems.product_id,
          factory_id: factoryId || "",
          product_name: skuItems.product_name || "",
          sku_id: values.product_sku_id,
          sku_name: skuItems.sku_name,
          sku_image: skuItems.sku_image,
          price_id: skuItems.price_id,
          factory_price: Number(skuItems.factory_price),
          wholesale_price: Number(skuItems.wholesale_price),
          retail_price: Number(skuItems.retail_price),
          vip_price: Number(skuItems.vip_price),
          qty: Number(values.qty || 1),
          product_quantity: Number(skuItems.product_quantity),
          comment_text: values.comment_text,
        };
        setDataTemporary((prevData) => [...prevData, newData]);
        setSelectSkuId(undefined);
        form.resetFields();

      }
      // }
    }
    onClose();
   // form.resetFields();
  };

  /** options สำหรับ Select — memoize เพื่อไม่สร้าง element ใหม่ทุก render */
  const skuSelectOptions = useMemo(
    () =>
      (dataSku?.data ?? []).map((values: GetDataSkuListItems) => ({
        value: values.sku_id,
        label: `${values.product_sku_code ? `(${values.product_sku_code}) ` : ""}${values.product_name} ${values.sku_name} (${numberFormat(
          refType === "retail" ? values.wholesale_price : values.factory_price
        )})`,
      })),
    [dataSku?.data, refType]
  );

  useEffect(() => {
    //check is promptpay
    console.log("order_payment_type", order_payment_type);
    if (order_payment_type === "promptpay" || order_payment_type === "credit") {
      setIsKsher(true);
    } else {
      setIsKsher(false);
    }
  }, [order_payment_type]);

 // console.log("order_payment_type--->", order_payment_type);

  return (
    <>
      <FormWrapper>
        <HeadWrapper>
          <Typography.Text
            style={{
              fontWeight: 600,
              whiteSpace: "nowrap",
            }}
            underline
          >
            สินค้า
          </Typography.Text>
          <ButtonWrapper>
            {canRepeat && (
              <RepeatOrderButton
                disabled={userData?.id ? false : true}
                onClick={repeatOrderRetail}
                type="primary"
              >
                ซื้อซ้ำ
              </RepeatOrderButton>
            )}
            {canCreate && !isKsher && (
              <StyledButton
                onClick={showDrawer}
                disabled={userData?.id ? false : true}
                type="primary"
              >
                <PlusOutlined /> เพิ่มแถวรายการ
              </StyledButton>
            )}
          </ButtonWrapper>
        </HeadWrapper>

        <Row >
          <Col span={24}>
            <OrderReserveProductTable
              refType={refType}
              canDelete={canDelete && !isKsher}
              data={dataTemporary}
              setData={setDataTemporary}
              showStatusColumn={showStatusColumn}
              editingKey={editingKey}
              setEditingKey={setEditingKey}
              currentSelected={currentSelected}
              setCurrentSelected={setCurrentSelected}
              userData={userData}
              isKsher={isKsher}
              item={item}
            />
          </Col>
        </Row>
      </FormWrapper>
      <Modal
        title="เพิ่มรายการ"
        open={open}
        width={800}
        maskClosable={false}
        onCancel={onClose}
        style={{ top: 20 }}
        onOk={() => {
          form.submit();
        }}
        cancelText="ยกเลิก"
        okText="เพิ่ม"
      >
        <Form form={form} {...formItemLayout} onFinish={onFinish}>
          <FormWrapper>
            <FormItems
              label="รายการสินค้า"
              labelAlign="left"
              name="product_sku_id"
              rules={[{ required: true, message: "รายการสินค้า" }]}
            >
              <StyledSelect
                showSearch
                placeholder="เลือกรายการสินค้า"
                options={skuSelectOptions}
                /** ปิด virtual scroll — ข้อความ option ยาวกว่า listItemHeight ค่าเริ่มต้น (24px) ทำให้ scroll เร็วแล้วแสดงรายการซ้ำ */
                virtual={false}
                onSelect={(value) => {
                  setSelectSkuId(value as string);
                  form.setFieldsValue({ qty: 1 });
                }}
              />
            </FormItems>
            {selectSkuid && selectedSku?.is_stock && selectedSku.stock && (
              <StockHintRow>
                <Typography.Text type="secondary">
                  สต็อกพร้อมขาย:{" "}
                  <Typography.Text strong>
                    {numberFormat(selectedSku.stock.stock_balance)} ชิ้น
                  </Typography.Text>
                  {selectedSku.product_quantity > 1 && (
                    <>
                      {" "}
                      ({numberFormat(selectedSku.product_quantity)} ชิ้น/แพ็ก)
                    </>
                  )}
                  {maxOrderablePackQty !== null && (
                    <>
                      {" · "}
                      สั่งได้สูงสุด{" "}
                      <Typography.Text strong type={maxOrderablePackQty > 0 ? undefined : "danger"}>
                        {maxOrderablePackQty} แพ็ก
                      </Typography.Text>
                      {existingCartPackQty > 0 && (
                        <> (มีในตะกร้า {existingCartPackQty} แพ็กแล้ว)</>
                      )}
                    </>
                  )}
                </Typography.Text>
              </StockHintRow>
            )}
            {selectSkuid && (
              <FormItems
                label="จำนวน"
                labelAlign="left"
                name="qty"
                initialValue={1}
                rules={[
                  {
                    validator: (_, value) => {
                      if (value == null || value === "" || Number(value) < 1) {
                        return Promise.reject(new Error("จำนวนต้องมากกว่า 0"));
                      }
                      const err = validateOrderQtyAgainstStock(
                        selectedSku,
                        Number(value),
                        existingCartPackQty
                      );
                      return err ? Promise.reject(new Error(err)) : Promise.resolve();
                    },
                  },
                ]}
              >
                <Input
                  placeholder="จำนวน"
                  type="number"
                  autoComplete="off"
                  min={1}
                  max={
                    maxOrderablePackQty != null
                      ? Math.max(1, maxOrderablePackQty - existingCartPackQty)
                      : undefined
                  }
                  disabled={
                    selectedSku?.is_stock === true &&
                    maxOrderablePackQty !== null &&
                    maxOrderablePackQty - existingCartPackQty <= 0
                  }
                />
              </FormItems>
            )}
            {selectSkuid && (
              <FormItems
                label="โน๊ตผู้ประกอบการ"
                labelAlign="left"
                name="comment_text"
              >
                <Input.TextArea
                  placeholder="โน๊ตผู้ประกอบการ"
                  autoSize={{ minRows: 2, maxRows: 10 }}
                  autoComplete="off"
                  size="middle"
                />
              </FormItems>
            )}
          </FormWrapper>
        </Form>
      </Modal>
    </>
  );
};

export default MainCreateOrderProduct;

const StyledSelect = styled(Select)`
  .ant-select-selection-item {
    white-space: normal !important;
  }
`;

const StyledButton = styled(Button)`
  margin-right: 5px;
`;

const RepeatOrderButton = styled(Button)`
  width: 140px;
  margin-right: 5px;
`;

const FormItems = styled(Form.Item)`
  margin-bottom: 10px;
  width: 100%;
`;
const ButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  width: 100%;
  height: 100%;
`;

const HeadWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  height: 100%;
  padding: 0px 10px 0px 10px;
  margin-bottom: 10px;
  & > :first-child {
    flex: 3;
  }

  & > :last-child {
    flex: 7;
    display: flex;
    justify-content: flex-end;
  }
`;
const FormWrapper = styled.div`
  width: 100%;
  height: 100%;
  background-color: #ffffffff;
`;

const StockHintRow = styled.div`
  margin: -4px 0 12px 33.33%;
  padding-right: 16px;
  @media (max-width: 576px) {
    margin-left: 0;
    padding: 0 4px 8px;
  }
`;
